import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  GitBranch, Plus, Edit2, Trash2, ArrowLeft, RefreshCw,
  Play, ChevronDown, ChevronUp, Settings, Save
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

export default function Flows() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  
  const [agent, setAgent] = useState(null);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [expandedFlow, setExpandedFlow] = useState(null);

  useEffect(() => {
    loadData();
  }, [agentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const agentRes = await api.get(`/agents/${agentId}`);
      setAgent(agentRes.data.agent || agentRes.data);
      
      const flowsRes = await api.get(`/flows/${agentId}`);
      setFlows(flowsRes.data.data || []);
    } catch (error) {
      console.error('Load flows error:', error);
      toast.error('Failed to load flows');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFlow = () => {
    setEditingFlow({
      name: '',
      flow_key: '',
      description: '',
      trigger_intents: [],
      steps: [],
      starting_message: '',
      completion_action: 'message',
      completion_message: 'Thank you! Is there anything else I can help with?',
      completion_queue: '',
      is_active: true
    });
    setShowModal(true);
  };

  const handleEditFlow = async (flow) => {
    try {
      // Load full flow with steps
      const res = await api.get(`/flows/${agentId}/${flow.id}`);
      setEditingFlow(res.data.data || flow);
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load flow details');
    }
  };

  const handleSaveFlow = async () => {
    try {
      if (!editingFlow.name) {
        toast.error('Flow name is required');
        return;
      }
      
      if (editingFlow.id) {
        await api.put(`/flows/${agentId}/${editingFlow.id}`, editingFlow);
        toast.success('Flow updated');
      } else {
        await api.post(`/flows/${agentId}`, editingFlow);
        toast.success('Flow created');
      }
      
      setShowModal(false);
      setEditingFlow(null);
      loadData();
    } catch (error) {
      toast.error('Failed to save flow');
    }
  };

  const handleDeleteFlow = async (flowId) => {
    if (!confirm('Are you sure you want to delete this flow?')) return;
    
    try {
      await api.delete(`/flows/${agentId}/${flowId}`);
      toast.success('Flow deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete flow');
    }
  };

  const toggleFlowActive = async (flow) => {
    try {
      await api.put(`/flows/${agentId}/${flow.id}`, {
        is_active: !flow.is_active
      });
      loadData();
    } catch (error) {
      toast.error('Failed to update flow');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/agents/${agentId}/ivr`)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Conversation Flows</h1>
            <p className="text-gray-600">{agent?.name}</p>
          </div>
        </div>
        
        <button
          onClick={handleCreateFlow}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Flow
        </button>
      </div>

      {/* Flows List */}
      {flows.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <GitBranch className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No flows yet</h3>
          <p className="text-gray-500 mb-4">
            Create conversation flows to collect information from callers step by step
          </p>
          <button
            onClick={handleCreateFlow}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create First Flow
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {flows.map((flow) => (
            <div key={flow.id} className="bg-white rounded-xl shadow-sm border">
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${flow.is_active ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                      <GitBranch className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">{flow.name}</h3>
                        <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                          {flow.flow_key}
                        </span>
                        {!flow.is_active && (
                          <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                            Disabled
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{flow.description || 'No description'}</p>
                      
                      {flow.trigger_intents && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(typeof flow.trigger_intents === 'string' 
                            ? (flow.trigger_intents ? JSON.parse(flow.trigger_intents) : [])
                            : (flow.trigger_intents || [])
                          ).map((intent, idx) => (
                            <span key={idx} className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded">
                              {intent}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setExpandedFlow(expandedFlow === flow.id ? null : flow.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      {expandedFlow === flow.id ? (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                    <button
                      onClick={() => toggleFlowActive(flow)}
                      className={`px-3 py-1 text-sm rounded ${
                        flow.is_active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {flow.is_active ? 'Active' : 'Inactive'}
                    </button>
                    <button
                      onClick={() => handleEditFlow(flow)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <Edit2 className="w-4 h-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => handleDeleteFlow(flow.id)}
                      className="p-2 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Expanded Steps View */}
              {expandedFlow === flow.id && (
                <div className="border-t bg-gray-50 p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Flow Steps</h4>
                  {flow.steps?.length > 0 ? (
                    <div className="space-y-2">
                      {flow.steps.map((step, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-3 border">
                          <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium">
                            {idx + 1}
                          </span>
                          <div className="flex-1">
                            <div className="font-medium text-sm">{step.name || `Step ${idx + 1}`}</div>
                            <div className="text-xs text-gray-500">
                              Slot: {step.slot_name || step.slot_config?.slot_name || 'N/A'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No steps configured</p>
                  )}
                  
                  <div className="mt-4 pt-4 border-t">
                    <div className="text-sm">
                      <span className="text-gray-500">Completion Action:</span>
                      <span className="ml-2 font-medium">{flow.completion_action || 'message'}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Flow Modal */}
      {showModal && editingFlow && (
        <FlowModal
          flow={editingFlow}
          onChange={setEditingFlow}
          onSave={handleSaveFlow}
          onClose={() => { setShowModal(false); setEditingFlow(null); }}
        />
      )}
    </div>
  );
}

// Flow Edit Modal
function FlowModal({ flow, onChange, onSave, onClose }) {
  const [triggerInput, setTriggerInput] = useState('');
  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [editingStepIndex, setEditingStepIndex] = useState(-1);

  const addTriggerIntent = () => {
    if (triggerInput.trim()) {
      onChange({
        ...flow,
        trigger_intents: [...(flow.trigger_intents || []), triggerInput.trim()]
      });
      setTriggerInput('');
    }
  };

  const removeTriggerIntent = (idx) => {
    const newIntents = [...flow.trigger_intents];
    newIntents.splice(idx, 1);
    onChange({ ...flow, trigger_intents: newIntents });
  };

  const addStep = () => {
    setEditingStep({
      name: '',
      slot_name: '',
      prompt: '',
      error_prompt: 'I didn\'t understand. Please try again.',
      validation_rules: {}
    });
    setEditingStepIndex(-1);
    setShowStepModal(true);
  };

  const editStep = (step, idx) => {
    setEditingStep({ ...step });
    setEditingStepIndex(idx);
    setShowStepModal(true);
  };

  const saveStep = () => {
    const steps = [...(flow.steps || [])];
    if (editingStepIndex >= 0) {
      steps[editingStepIndex] = editingStep;
    } else {
      steps.push({ ...editingStep, step_order: steps.length + 1 });
    }
    onChange({ ...flow, steps });
    setShowStepModal(false);
    setEditingStep(null);
  };

  const deleteStep = (idx) => {
    const steps = [...flow.steps];
    steps.splice(idx, 1);
    onChange({ ...flow, steps });
  };

  const moveStep = (idx, direction) => {
    const steps = [...flow.steps];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= steps.length) return;
    [steps[idx], steps[newIdx]] = [steps[newIdx], steps[idx]];
    onChange({ ...flow, steps });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">
            {flow.id ? 'Edit Flow' : 'Create Flow'}
          </h2>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={flow.name}
                onChange={(e) => onChange({ ...flow, name: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Account Verification"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Flow Key
              </label>
              <input
                type="text"
                value={flow.flow_key}
                onChange={(e) => onChange({ ...flow, flow_key: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="account_verification"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={flow.description || ''}
              onChange={(e) => onChange({ ...flow, description: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              rows={2}
            />
          </div>
          
          {/* Starting Message */}
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <label className="block text-sm font-medium text-green-800 mb-1">
              Starting Message (plays when flow begins)
            </label>
            <textarea
              value={flow.starting_message || ''}
              onChange={(e) => onChange({ ...flow, starting_message: e.target.value })}
              className="w-full border border-green-300 rounded-lg px-3 py-2"
              rows={2}
              placeholder="I'll help you with that. Let me collect some information..."
            />
          </div>
          
          {/* Trigger Intents */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trigger Intents
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={triggerInput}
                onChange={(e) => setTriggerInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTriggerIntent())}
                className="flex-1 border rounded-lg px-3 py-2"
                placeholder="Enter intent key and press Enter"
              />
              <button
                type="button"
                onClick={addTriggerIntent}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(flow.trigger_intents || []).map((intent, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm"
                >
                  {intent}
                  <button
                    type="button"
                    onClick={() => removeTriggerIntent(idx)}
                    className="hover:text-purple-900"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
          
          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Flow Steps ({(flow.steps || []).length})
              </label>
              <button
                type="button"
                onClick={addStep}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="w-4 h-4" />
                Add Step
              </button>
            </div>
            
            {(flow.steps || []).length === 0 ? (
              <div className="border-2 border-dashed rounded-lg p-6 text-center text-gray-500">
                No steps configured. Add steps to collect information.
              </div>
            ) : (
              <div className="space-y-2">
                {flow.steps.map((step, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => moveStep(idx, -1)}
                        disabled={idx === 0}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      >
                        <ChevronUp className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveStep(idx, 1)}
                        disabled={idx === flow.steps.length - 1}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      >
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium">
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{step.name || `Step ${idx + 1}`}</div>
                      <div className="text-xs text-gray-500">
                        Slot: {step.slot_name} • Prompt: {step.prompt?.substring(0, 40)}...
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => editStep(step, idx)}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      <Edit2 className="w-4 h-4 text-gray-500" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteStep(idx)}
                      className="p-1 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Completion */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="text-sm font-semibold text-blue-800 mb-3">Flow Completion</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">
                  When flow completes, do:
                </label>
                <select
                  value={flow.completion_action || 'message'}
                  onChange={(e) => onChange({ ...flow, completion_action: e.target.value })}
                  className="w-full border border-blue-300 rounded-lg px-3 py-2"
                >
                  <option value="message">Play Completion Message</option>
                  <option value="transfer">Transfer to Agent Queue</option>
                  <option value="end">End Call with Goodbye</option>
                  <option value="return">Return to Main Menu</option>
                </select>
              </div>
              
              {(flow.completion_action === 'message' || flow.completion_action === 'end' || flow.completion_action === 'return' || !flow.completion_action) && (
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Completion Message
                  </label>
                  <textarea
                    value={flow.completion_message || ''}
                    onChange={(e) => onChange({ ...flow, completion_message: e.target.value })}
                    className="w-full border border-blue-300 rounded-lg px-3 py-2"
                    rows={2}
                    placeholder="Thank you! I've collected all the information. Is there anything else I can help with?"
                  />
                </div>
              )}
              
              {flow.completion_action === 'transfer' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">
                      Transfer Queue
                    </label>
                    <input
                      type="text"
                      value={flow.completion_queue || ''}
                      onChange={(e) => onChange({ ...flow, completion_queue: e.target.value })}
                      className="w-full border border-blue-300 rounded-lg px-3 py-2"
                      placeholder="support-queue"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">
                      Transfer Message
                    </label>
                    <textarea
                      value={flow.completion_message || ''}
                      onChange={(e) => onChange({ ...flow, completion_message: e.target.value })}
                      className="w-full border border-blue-300 rounded-lg px-3 py-2"
                      rows={2}
                      placeholder="I've collected your information. Let me transfer you to an agent."
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Save className="w-4 h-4" />
            {flow.id ? 'Save Changes' : 'Create Flow'}
          </button>
        </div>
        
        {/* Step Modal */}
        {showStepModal && editingStep && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold">
                  {editingStepIndex >= 0 ? 'Edit Step' : 'Add Step'}
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Step Name
                  </label>
                  <input
                    type="text"
                    value={editingStep.name || ''}
                    onChange={(e) => setEditingStep({ ...editingStep, name: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Get Phone Number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slot Name *
                  </label>
                  <input
                    type="text"
                    value={editingStep.slot_name || ''}
                    onChange={(e) => setEditingStep({ ...editingStep, slot_name: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="phone_number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt *
                  </label>
                  <textarea
                    value={editingStep.prompt || ''}
                    onChange={(e) => setEditingStep({ ...editingStep, prompt: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    rows={2}
                    placeholder="Please tell me your phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Error Prompt
                  </label>
                  <textarea
                    value={editingStep.error_prompt || ''}
                    onChange={(e) => setEditingStep({ ...editingStep, error_prompt: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    rows={2}
                    placeholder="I didn't understand. Please try again."
                  />
                </div>
              </div>
              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={() => { setShowStepModal(false); setEditingStep(null); }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveStep}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingStepIndex >= 0 ? 'Update Step' : 'Add Step'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
